import React, { useEffect, useState, useMemo } from 'react';

// Import section components
import BusinessInformationSection from './sections/BusinessInformationSection';
import ContactInformationSection from './sections/ContactInformationSection';
import TradingAddressSection from './sections/TradingAddressSection';
import RegisteredAddressSection from './sections/RegisteredAddressSection';
import SoleTraderDetailsSection from './sections/SoleTraderDetailsSection';
import TradingDurationSection from './sections/TradingDurationSection';
import HomeAddressSection from './sections/HomeAddressSection';
import AddressHistorySection from './sections/AddressHistorySection';
import InternalInformationSection from './sections/InternalInformationSection';
import CompaniesHouseSection from './sections/CompaniesHouseSection';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Alert,
  AlertTitle,
  LinearProgress,
  Typography,
  Box,
} from '@mui/material';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { useClientContext } from '../../context/ClientContext';
import { displayError } from '../../utils/errorHandler';
import { validateClientCreation } from '../../utils/validation';
import toast from 'react-hot-toast';
import { Client, BusinessType, CreateClientDTO, ClientFormValues } from '../../types/client';
import { fuzzyMatchSoleTraders, FuzzyMatchResult } from '../../api/clients';
import { checkExistingClient } from '../../api/clients';
import { Lookup } from '../../types/lookup.types';
import { debounce } from 'lodash';
import { getRepresentatives } from '../../api/users';
import { getActiveLookupsByType } from '../../api/lookups';
import { User } from '../../types/user.types';
import { useNavigate } from 'react-router-dom';
import { CompanyResult } from '../../types/company.types';

// UK phone number regex pattern
const UK_PHONE_REGEX = /^(?:(?:\+44)|(?:0))(?:(?:(?:[1-9][0-9]{8})|(?:(?:[1-9][0-9]{7})|(?:[1-9][0-9]{9})|(?:[1-9][0-9]{2}\s[0-9]{4}\s[0-9]{4})|(?:[1-9][0-9]{2}\s[0-9]{3}\s[0-9]{4})))$)/;

// UK postcode regex pattern
const UK_POSTCODE_REGEX = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;

// Use the imported BusinessType from API types
const BUSINESS_TYPES: BusinessType[] = ['Limited Company', 'Sole Trader', 'Charity / Trust / Community Org'];

interface AddClientDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (clientData: CreateClientDTO) => Promise<void>;
  initialData?: Partial<Client>;
}

// Define a shared FormValues interface that's compatible with all section components
// Using the shared ClientFormValues interface from client.ts

// Local interface extending the shared interface if needed
interface FormValues extends ClientFormValues {
  // Any additional fields specific to AddClientDialog can be added here
  // All common fields are already defined in ClientFormValues
}

const calculateAge = (year: string, month: string, day: string): number | null => {
  if (!day || !month || !year) return null;
  const birthDate = new Date(`${year}-${month}-${day}`);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const validationSchema = Yup.object({
  companyName: Yup.string().required('Company name is required'),
  ownerName: Yup.string().required('Owner name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phoneNumber: Yup.string()
    .transform((value) => value?.replace(/\s+/g, '') || value)
    .matches(UK_PHONE_REGEX, 'Please enter a valid UK phone number')
    .required('Phone number is required'),
  businessType: Yup.string()
    .oneOf(BUSINESS_TYPES, 'Invalid business type')
    .required('Business type is required'),
  industry: Yup.string().required('Industry is required'),
  position: Yup.string().required('Position is required'),
  representativeId: Yup.string().required('Representative is required'),
  // Trading Address - required for all business types
  tradingAddressLine1: Yup.string().required('Trading address line 1 is required'),
  tradingAddressLine2: Yup.string().optional(),
  tradingCity: Yup.string().required('Trading city is required'),
  tradingCounty: Yup.string().optional(),
  tradingPostcode: Yup.string()
    .matches(UK_POSTCODE_REGEX, 'Please enter a valid UK postcode')
    .required('Trading postcode is required'),
  // Registered Business Address - required for Limited Companies and Charities
  registeredAddressLine1: Yup.string().when('businessType', {
    is: (val: string) => ['Limited Company', 'Charity / Trust / Community Org'].includes(val),
    then: (schema) => schema.required('Registered address line 1 is required'),
    otherwise: (schema) => schema.optional()
  }),
  registeredAddressLine2: Yup.string().optional(),
  registeredCity: Yup.string().when('businessType', {
    is: (val: string) => ['Limited Company', 'Charity / Trust / Community Org'].includes(val),
    then: (schema) => schema.required('Registered city is required'),
    otherwise: (schema) => schema.optional()
  }),
  registeredCounty: Yup.string().optional(),
  registeredPostcode: Yup.string().when('businessType', {
    is: (val: string) => ['Limited Company', 'Charity / Trust / Community Org'].includes(val),
    then: (schema) => schema.matches(UK_POSTCODE_REGEX, 'Please enter a valid UK postcode').required('Registered postcode is required'),
    otherwise: (schema) => schema.optional()
  }),
  // Home Address - if any field is provided, all required fields become mandatory (for all business types)
  homeAddressLine1: Yup.string().test('required-if-any', 'Home address line 1 is required if any home address field is provided', function(value) {
    const { homeAddressLine2, homeCity, homeCounty, homePostcode } = this.parent;
    const anyFieldProvided = Boolean(value || homeAddressLine2 || homeCity || homeCounty || homePostcode);
    return !anyFieldProvided || Boolean(value);
  }),
  homeAddressLine2: Yup.string().optional(),
  homeCity: Yup.string().test('required-if-any', 'Home city is required if any home address field is provided', function(value) {
    const { homeAddressLine1, homeAddressLine2, homeCounty, homePostcode } = this.parent;
    const anyFieldProvided = Boolean(homeAddressLine1 || homeAddressLine2 || value || homeCounty || homePostcode);
    return !anyFieldProvided || Boolean(value);
  }),
  homeCounty: Yup.string().optional(),
  homePostcode: Yup.string().test('required-if-any', 'Home postcode is required and must be a valid UK postcode', function(value) {
    const { homeAddressLine1, homeAddressLine2, homeCity, homeCounty } = this.parent;
    const anyFieldProvided = Boolean(homeAddressLine1 || homeAddressLine2 || homeCity || homeCounty || value);
    if (!anyFieldProvided) return true;
    if (!value) return false;
    return UK_POSTCODE_REGEX.test(value);
  }),
  // Company fields
  companyNumber: Yup.string().when('businessType', {
    is: (val: string) => ['Limited Company', 'Charity / Trust / Community Org'].includes(val),
    then: (schema) => schema.required('Company number is required'),
    otherwise: (schema) => schema.optional()
  }),
  incorporationDate: Yup.string().when('businessType', {
    is: (val: string) => ['Limited Company', 'Charity / Trust / Community Org'].includes(val),
    then: (schema) => schema.required('Incorporation date is required'),
    otherwise: (schema) => schema.optional()
  }),
  // Sole Trader fields (all optional)
  dobDay: Yup.string().optional(),
  dobMonth: Yup.string().optional(),
  dobYear: Yup.string().when(['dobDay', 'dobMonth'], {
    is: (day: string, month: string) => Boolean(day || month),
    then: (schema) => schema.required('Year is required if day or month is provided'),
    otherwise: (schema) => schema.optional()
  }).test('valid-age', 'Must be at least 18 years old', function(value) {
    if (!value || !this.parent.dobMonth || !this.parent.dobDay) return true;
    const dobMonth = this.parent.dobMonth;
    const dobDay = this.parent.dobDay;
    const age = calculateAge(value, dobMonth, dobDay);
    return age !== null && age >= 18;
  }),
  yearsTrading: Yup.string()
    .matches(/^\d*$/, 'Must be a number')
    .test('non-negative', 'Must be 0 or greater', value => !value || parseInt(value) >= 0)
    .optional(),
  monthsTrading: Yup.string()
    .matches(/^\d*$/, 'Must be a number')
    .test('max-months', 'Must be between 0 and 11', value => !value || (parseInt(value) >= 0 && parseInt(value) <= 11))
    .optional(),
  notes: Yup.string().optional()
});

const AddClientDialog: React.FC<AddClientDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData
}) => {
  const { createClient } = useClientContext();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [partners, setPartners] = useState<Lookup[]>([]);
  const [industries, setIndustries] = useState<Lookup[]>([]);
  const [position, setPosition] = useState<Lookup[]>([]);
  const [representatives, setRepresentatives] = useState<User[]>([]);
  const [existingClientEmail, setExistingClientEmail] = useState<Client | null>(null);
  const [existingClientPhone, setExistingClientPhone] = useState<Client | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<CompanyResult | null>(null);
  const [duplicateClient, setDuplicateClient] = useState<{exists: boolean; message: string; clientId?: string} | null>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState<boolean>(false);
  const [fuzzyMatches, setFuzzyMatches] = useState<FuzzyMatchResult[]>([]);
  const [showFuzzyMatchDialog, setShowFuzzyMatchDialog] = useState<boolean>(false);

  // Debounced function to check for existing clients by email or phone
  const debouncedCheckExistingClient = useMemo(
    () => debounce((field: string, value: string, setExisting: (exists: boolean) => void) => {
      if (!value) {
        setExisting(false);
        return;
      }

      // Build the dynamic key-value pair for the API call
      const params: Record<string, string> = {};
      params[field] = value;

      checkExistingClient(params)
        .then((response) => {
          if (response && response.exists) {
            setExisting(true);
          } else {
            setExisting(false);
          }
        })
        .catch(() => {
          setExisting(false);
        });
    }, 500),
    []
  );

  // Debounced fuzzy match for Sole Traders
  const debouncedFuzzyMatch = useMemo(
    () => debounce((formValues: any) => {
      if (formValues.businessType !== 'Sole Trader') {
        return;
      }

      // Need at least owner name or company name
      if (!formValues.ownerName && !formValues.companyName) {
        return;
      }

      fuzzyMatchSoleTraders({
        ownerName: formValues.ownerName,
        companyName: formValues.companyName,
        email: formValues.email,
        phoneNumber: formValues.phoneNumber,
        postcode: formValues.tradingPostcode,
        businessType: formValues.businessType
      })
        .then((response) => {
          if (response.matches && response.matches.length > 0) {
            setFuzzyMatches(response.matches);
            setShowFuzzyMatchDialog(true);
          } else {
            setFuzzyMatches([]);
          }
        })
        .catch((error) => {
          console.error('Fuzzy match error:', error);
          setFuzzyMatches([]);
        });
    }, 1000),
    []
  );

  // Load lookups and representatives on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partnersData, industriesData, positionsData, representativesData] = await Promise.all([
          getActiveLookupsByType('Partners'), // Match capitalization with EditClientDialog
          getActiveLookupsByType('Industry'),
          getActiveLookupsByType('Positions'),
          getRepresentatives()
        ]);
        
        setPartners(partnersData);
        setIndustries(industriesData);
        setPosition(positionsData);
        setRepresentatives(representativesData);
      } catch (err) {
        console.error('Error fetching lookups:', err);
        setError('Failed to load required data. Please try again.');
      }
    };

    fetchData();
  }, []);

  // Handle company selection from Companies House search
  const handleCompanySelect = async (company: CompanyResult) => {
    setSelectedCompany(company);
    
    // Check if client already exists with this company number
    try {
      const result = await checkExistingClient({ companyNumber: company.company_number });
      if (result.exists) {
        setDuplicateClient(result);
        setShowDuplicateDialog(true);
        return; // Don't populate form if duplicate found
      }
    } catch (error) {
      console.error('Error checking for duplicate client:', error);
      // Continue with form population even if check fails
    }
    
    // Use type assertion to handle potential structure differences
    // CompanyResult might have different structures based on API version
    const address = (company as any).registered_office_address || {};
    
    // Build address line 1 with premises (building number) if available
    const addressLine1Parts = [address.premises, address.address_line_1].filter(Boolean);
    const addressLine1 = addressLine1Parts.join(' ');
    
    // Use type assertion to make TypeScript happy with the formik props
    const formikProps = formik as unknown as FormikProps<FormValues>;
    (formikProps as any).setValues({
      // Cast to any to avoid type conflicts between different FormValues interfaces
      ...formikProps.values,
      companyName: company.company_name,
      companyNumber: company.company_number,
      incorporationDate: company.date_of_creation,
      registeredAddressLine1: addressLine1 || '',
      registeredAddressLine2: address.address_line_2 || '',
      registeredCity: address.locality || '',
      registeredCounty: address.county || address.region || '',
      registeredPostcode: address.postal_code || ''
    } as any);
  };

  const handleClose = () => {
    formik.resetForm();
    setExistingClientEmail(null);
    setExistingClientPhone(null);
    setSelectedCompany(null);
    onClose();
  };

  const handleSubmit = async (values: FormValues) => {
    console.log('handleSubmit function called with values:', values);
    try {
      setLoading(true);
      setError(null);
      
      // Client-side validation
      const validationResult = validateClientCreation({
        businessType: values.businessType,
        companyName: values.companyName,
        ownerName: values.ownerName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        registeredPostcode: values.registeredPostcode,
        tradingPostcode: values.tradingPostcode
      });

      if (!validationResult.isValid) {
        // Display all validation errors
        const errorCount = Object.keys(validationResult.errors).length;
        const errorList = Object.entries(validationResult.errors)
          .map(([field, message]) => `• ${message}`)
          .join('\n');
        
        setError(
          errorCount === 1 
            ? Object.values(validationResult.errors)[0]
            : `Please fix the following ${errorCount} errors:\n${errorList}`
        );
        setLoading(false);
        
        // Scroll to top to show error message
        document.querySelector('[role="dialog"]')?.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      
      // Check for existing client conflicts
      if (existingClientEmail || existingClientPhone) {
        const conflicts = [];
        if (existingClientEmail) conflicts.push(`email (${existingClientEmail.email})`);
        if (existingClientPhone) conflicts.push(`phone number (${existingClientPhone.phoneNumber})`);
        
        setError(
          `Cannot create client: A client with this ${conflicts.join(' and ')} already exists in the system. ` +
          `Please use a different ${conflicts.length > 1 ? 'email and phone number' : conflicts[0].split(' ')[0]}.`
        );
        setLoading(false);
        
        // Scroll to top to show error message
        document.querySelector('[role="dialog"]')?.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      
      // Prepare client data for API
      const clientData: CreateClientDTO = {
        businessType: values.businessType,
        companyName: values.companyName,
        ownerName: values.ownerName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        position: values.position,
        industry: values.industry,
        representativeId: values.representativeId || values.representative || '',
        partner: values.partner || undefined,
        
        tradingAddress: {
          line1: values.tradingAddressLine1,
          line2: values.tradingAddressLine2,
          city: values.tradingCity,
          county: values.tradingCounty,
          postcode: values.tradingPostcode,
          country: 'United Kingdom'
        },
        
        // Only include registered address for business types that require it
        ...((['Limited Company', 'Charity / Trust / Community Org'].includes(values.businessType)) ? {
          registeredBusinessAddress: {
            line1: values.registeredAddressLine1 || '',
            line2: values.registeredAddressLine2,
            city: values.registeredCity || '',
            county: values.registeredCounty,
            postcode: values.registeredPostcode || ''
          }
        } : {}),
        
        // Include home address for all business types if provided
        ...(values.homeAddressLine1 ? {
          homeAddress: {
            line1: values.homeAddressLine1,
            line2: values.homeAddressLine2,
            city: values.homeCity || '',
            county: values.homeCounty,
            postcode: values.homePostcode || '',
            movedInDate: values.homeMovedInDate ? values.homeMovedInDate.toISOString() : undefined
          }
        } : {}),
        
        // Include business type specific fields
        ...((['Limited Company', 'Charity / Trust / Community Org'].includes(values.businessType)) ? {
          companyNumber: values.companyNumber,
          incorporationDate: values.incorporationDate
        } : {}),
        
        // Include sole trader specific fields
        ...(values.businessType === 'Sole Trader' ? {
          // Construct date of birth from individual components if needed
          dateOfBirth: values.dateOfBirth || 
            (values.dobYear && values.dobMonth && values.dobDay ? 
              `${values.dobYear}-${values.dobMonth}-${String(values.dobDay).padStart(2, '0')}` : 
              undefined),
          addressHistory: values.addressHistory && values.addressHistory.length > 0 ? values.addressHistory : undefined
        } : {}),
        
        // Include trading duration for all business types
        yearsTrading: values.yearsTrading ? parseInt(values.yearsTrading) : undefined,
        monthsTrading: values.monthsTrading ? parseInt(values.monthsTrading) : undefined,
        
        // VAT information is handled separately in the backend
        notes: values.notes || ''
      };
      
      // Use provided onSubmit or default to createClient
      if (onSubmit) {
        await onSubmit(clientData);
        handleClose();
      } else {
        const newClient = await createClient(clientData);
        console.log('Client created successfully:', newClient);
        console.log('Navigating to:', `/utilities/clients/${newClient._id}`);
        
        // Show success message
        toast.success(
          `Client "${newClient.companyName}" created successfully!`,
          {
            duration: 4000,
            position: 'top-right',
            icon: '✅',
          }
        );
        
        // Close the dialog first to prevent UI conflicts
        formik.resetForm();
        setExistingClientEmail(null);
        setExistingClientPhone(null);
        setSelectedCompany(null);
        onClose();
        
        // Navigate to the client page using the _id property from MongoDB
        // Use setTimeout to ensure the dialog close animation completes
        setTimeout(() => {
          navigate(`/utilities/clients/${newClient._id}`);
        }, 100);
      }
    } catch (err: any) {
      console.error('Client creation error:', err);
      
      // Parse the error to get detailed information
      displayError(err);
      
      // Build a user-friendly error message
      let errorMessage = 'Failed to create client. ';
      
      if (err.response?.data?.message) {
        // Backend provided a specific error message
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.errors) {
        // Backend provided field-specific errors
        const fieldErrors = err.response.data.errors;
        const errorList = Object.entries(fieldErrors)
          .map(([field, msg]) => `• ${field}: ${msg}`)
          .join('\n');
        errorMessage = `Please fix the following errors:\n${errorList}`;
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid client data provided. Please check all required fields and try again.';
      } else if (err.response?.status === 409) {
        errorMessage = 'A client with this information already exists. Please check email, phone number, or company number.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again or contact support if the problem persists.';
      } else if (err.message) {
        errorMessage = err.message;
      } else {
        errorMessage = 'An unexpected error occurred. Please try again.';
      }
      
      setError(errorMessage);
      
      // Scroll to top to show error message
      document.querySelector('[role="dialog"]')?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  // Use explicit typing to avoid conflicts with other FormValues interfaces
  const formik = useFormik<FormValues>({
    initialValues: {
      businessType: initialData?.businessType || 'Limited Company',
      companyName: initialData?.companyName || '',
      ownerName: initialData?.ownerName || '',
      position: (initialData as any)?.position || '',
      email: initialData?.email || '',
      phoneNumber: initialData?.phoneNumber || '',
      
      tradingAddressLine1: initialData?.tradingAddress?.line1 || '',
      tradingAddressLine2: initialData?.tradingAddress?.line2 || '',
      tradingCity: initialData?.tradingAddress?.city || '',
      tradingCounty: initialData?.tradingAddress?.county || '',
      tradingPostcode: initialData?.tradingAddress?.postcode || '',
      
      registeredAddressLine1: initialData?.registeredBusinessAddress?.line1 || '',
      registeredAddressLine2: initialData?.registeredBusinessAddress?.line2 || '',
      registeredCity: initialData?.registeredBusinessAddress?.city || '',
      registeredCounty: initialData?.registeredBusinessAddress?.county || '',
      registeredPostcode: initialData?.registeredBusinessAddress?.postcode || '',
      
      companyNumber: '',
      incorporationDate: '',
      vatNumber: '',
      vatRegistered: false,
      dateOfBirth: '',
      monthsTrading: '',
      industry: '',
      representative: '',
      representativeId: '',
      partner: '',
      
      // Add fields needed for date components
      dobDay: '',
      dobMonth: '',
      dobYear: '',
      yearsTrading: '',
      
      // Optional fields with default values
      notes: initialData?.notes || '',
      homeAddressLine1: initialData?.homeAddress?.line1 || '',
      homeAddressLine2: initialData?.homeAddress?.line2 || '',
      homeCity: initialData?.homeAddress?.city || '',
      homeCounty: initialData?.homeAddress?.county || '',
      homePostcode: initialData?.homeAddress?.postcode || '',
      homeMovedInDate: initialData?.homeAddress?.movedInDate ? new Date(initialData.homeAddress.movedInDate) : null,
      addressHistory: initialData?.addressHistory || []
    },
    validationSchema,
    onSubmit: handleSubmit
  });

  // Function to calculate age from date of birth
  const calculateAge = (dateOfBirth: string): number => {
    if (!dateOfBirth) return 0;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Trigger fuzzy match when Sole Trader fields change
  useEffect(() => {
    if (formik.values.businessType === 'Sole Trader') {
      debouncedFuzzyMatch(formik.values);
    }
  }, [
    formik.values.ownerName,
    formik.values.companyName,
    formik.values.email,
    formik.values.phoneNumber,
    formik.values.tradingPostcode,
    formik.values.businessType,
    formik.values,
    debouncedFuzzyMatch
  ]);

  return (
    <>
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(25, 25, 25, 0.8)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          color: 'white',
        }
      }}
    >
      <DialogTitle sx={{ color: 'white' }}>Add New Client</DialogTitle>
      {loading && <LinearProgress />}
      {/* Use type assertion to resolve FormikProps conflicts */}
      <form onSubmit={(formik as any).handleSubmit}>
        <DialogContent sx={{ color: 'white' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(211, 47, 47, 0.15)', color: '#ff8a80' }}>
              <AlertTitle>Error</AlertTitle>
              {error}
            </Alert>
          )}
          
          {existingClientEmail && (
            <Alert severity="warning" sx={{ mb: 2, backgroundColor: 'rgba(237, 108, 2, 0.15)', color: '#ffb74d' }}>
              <AlertTitle>Existing Client Found</AlertTitle>
              A client with email {existingClientEmail.email} already exists.
            </Alert>
          )}
          
          {existingClientPhone && (
            <Alert severity="warning" sx={{ mb: 2, backgroundColor: 'rgba(237, 108, 2, 0.15)', color: '#ffb74d' }}>
              <AlertTitle>Existing Client Found</AlertTitle>
              A client with phone number {existingClientPhone.phoneNumber} already exists.
            </Alert>
          )}
          
          <Grid container spacing={2}>
            
            <BusinessInformationSection 
              formik={formik as any} 
              isEditing={false}
              businessTypes={[
                'Limited Company',
                'Sole Trader',
                'Charity / Trust / Community Org'
              ] as BusinessType[]}
            />
            
            {['Limited Company', 'Charity / Trust / Community Org'].includes(formik.values.businessType) && (
              <>
                
                <CompaniesHouseSection 
                  formik={formik as any}
                  onCompanySelect={(company) => {
                    if (company) handleCompanySelect(company);
                  }}
                  selectedCompany={selectedCompany}
                />
                
                <RegisteredAddressSection 
                  formik={formik as any}
                />
              </>
            )}
            
            {formik.values.businessType === 'Sole Trader' && (
              <TradingDurationSection formik={formik as any} />
            )}
            
            <HomeAddressSection formik={formik as any} />
            
            {formik.values.businessType === 'Sole Trader' && (
              <>
                <AddressHistorySection formik={formik as any} />
                
                <SoleTraderDetailsSection 
                  formik={formik as any}
                  calculateAge={calculateAge}
                />
              </>
            )}

            <TradingAddressSection formik={formik as any} showCopyButton={true} />
            
            <ContactInformationSection 
              formik={formik as any} 
              position={position.map(p => p.value)}
              onEmailBlur={(value) => {
                if (value && !formik.errors.email) {
                  debouncedCheckExistingClient('email', value, setExistingClientEmail);
                }
              }}
              onPhoneBlur={(value) => {
                if (value && !formik.errors.phoneNumber) {
                  debouncedCheckExistingClient('phoneNumber', value, setExistingClientPhone);
                }
              }}
              existingClientEmail={existingClientEmail}
              existingClientPhone={existingClientPhone}
            />

            <InternalInformationSection 
              formik={formik as any} 
              representatives={representatives}
              industries={industries.map(i => i.value)}
              partners={partners.map(p => p.value)}
              isAdmin={true} // Allow anyone to set representative when creating a client
            />

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="notes"
                name="notes"
                label="Notes"
                multiline
                rows={4}
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.notes && Boolean(formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiInputBase-input': {
                    color: 'white',
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={handleClose}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            type="button" 
            variant="contained" 
            color="primary" 
            disabled={loading}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              backdropFilter: 'blur(4px)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
            onClick={() => {
              console.log('Submit button clicked');
              console.log('Formik state:', formik);
              console.log('Formik values:', formik.values);
              console.log('Formik errors:', formik.errors);
              console.log('Formik isValid:', formik.isValid);
              try {
                formik.handleSubmit();
                console.log('handleSubmit called successfully');
              } catch (error) {
                console.error('Error in handleSubmit:', error);
              }
            }}
          >
            Add Client
          </Button>
        </DialogActions>
      </form>
    </Dialog>
    
    {/* Duplicate Client Warning Dialog */}
    <Dialog
      open={showDuplicateDialog}
      onClose={() => setShowDuplicateDialog(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        }
      }}
    >
      <DialogTitle sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        Client Already Exists
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {duplicateClient?.message || 'A client with this company number already exists in the system.'}
        </Alert>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}>
          Would you like to edit the existing client instead?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Button
          onClick={() => {
            setShowDuplicateDialog(false);
            setDuplicateClient(null);
          }}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (duplicateClient?.clientId) {
              navigate(`/utilities/clients/${duplicateClient.clientId}`);
              handleClose();
              setShowDuplicateDialog(false);
            }
          }}
          sx={{
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          Edit Existing Client
        </Button>
      </DialogActions>
    </Dialog>

    {/* Fuzzy Match Dialog for Sole Traders */}
    <Dialog
      open={showFuzzyMatchDialog}
      onClose={() => setShowFuzzyMatchDialog(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        }
      }}
    >
      <DialogTitle sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        Potential Duplicate Sole Traders Found
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          We found {fuzzyMatches.length} similar Sole Trader(s) in the system. Please review to avoid duplicates.
        </Alert>
        <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
          {fuzzyMatches.map((match, index) => (
            <Box
              key={match.clientId}
              sx={{
                p: 2,
                mb: 2,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  cursor: 'pointer'
                }
              }}
              onClick={() => {
                navigate(`/utilities/clients/${match.clientId}`);
                handleClose();
                setShowFuzzyMatchDialog(false);
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ color: 'white' }}>
                  {match.ownerName || match.companyName}
                </Typography>
                <Box
                  sx={{
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: match.matchScore >= 90 ? 'error.main' : match.matchScore >= 80 ? 'warning.main' : 'info.main',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                >
                  {match.matchScore}% Match
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                {match.companyName && match.ownerName && match.companyName !== match.ownerName && (
                  <>Company: {match.companyName}<br /></>
                )}
                {match.email && <>Email: {match.email}<br /></>}
                {match.phoneNumber && <>Phone: {match.phoneNumber}<br /></>}
                {match.postcode && <>Postcode: {match.postcode}</>}
              </Typography>
              <Box sx={{ mt: 1 }}>
                {match.matchReasons.map((reason, idx) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    sx={{
                      display: 'inline-block',
                      mr: 1,
                      px: 1,
                      py: 0.5,
                      borderRadius: 0.5,
                      backgroundColor: 'rgba(25, 118, 210, 0.2)',
                      color: 'primary.light'
                    }}
                  >
                    {reason}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Button
          onClick={() => setShowFuzzyMatchDialog(false)}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          Continue Anyway
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AddClientDialog;
